import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  FiLogOut,
  FiMonitor,
  FiRefreshCw,
  FiSearch,
  FiSmartphone,
} from "react-icons/fi";
import Modal from "../../components/common/Modal";
import loginSessionsService from "../../api/services/loginSessionsService";
import { confirmDanger } from "../../utils/confirmDialog";

const HISTORY_PAGE_SIZE = 20;

const PLATFORM_STYLES = {
  web: "bg-sky-100 text-sky-800",
  ios: "bg-gray-900 text-white",
  android: "bg-emerald-100 text-emerald-800",
  unknown: "bg-gray-100 text-gray-600",
};

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-800",
  revoked: "bg-rose-100 text-rose-800",
  expired: "bg-amber-100 text-amber-800",
};

const EMPTY_FILTERS = {
  search: "",
  platform: "all",
  status: "all",
  dateFrom: "",
  dateTo: "",
};

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function platformLabel(platform) {
  const p = String(platform || "unknown").toLowerCase();
  if (p === "ios") return "iOS";
  if (p === "android") return "Android";
  if (p === "web") return "Web";
  return p || "Unknown";
}

function deviceSummary(session) {
  if (session.deviceLabel) return session.deviceLabel;
  if (session.mobileModel) return session.mobileModel;
  const parts = [
    session.deviceManufacturer,
    session.deviceModel,
    session.deviceName,
  ].filter(Boolean);
  // Deduplicate e.g. "iPhone" + "iPhone"
  const unique = [...new Set(parts.map((p) => String(p).trim()))];
  if (unique.length) return unique.join(" · ");
  if (session.userAgent) {
    const ua = String(session.userAgent);
    if (ua.length > 56) return `${ua.slice(0, 56)}…`;
    return ua;
  }
  return session.deviceId || "Unknown device";
}

function osSummary(session) {
  if (session.osLabel) return session.osLabel;
  const os = session.deviceOs || "";
  const ver = session.osVersion || "";
  if (os && ver) return `${os} ${ver}`;
  return os || ver || "—";
}

function deviceKindLabel(session) {
  const kind = String(session.deviceKind || "").toLowerCase();
  if (kind === "phone") return "Mobile phone";
  if (kind === "tablet") return "Tablet";
  if (kind === "desktop") return "Computer / Laptop";
  if (kind === "browser") return "Mobile browser";
  const p = String(session.platform || "").toLowerCase();
  if (p === "ios" || p === "android") return "Mobile phone";
  if (p === "web") return "Computer / Laptop";
  return null;
}

function PlatformBadge({ platform }) {
  const key = String(platform || "unknown").toLowerCase();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        PLATFORM_STYLES[key] || PLATFORM_STYLES.unknown
      }`}
    >
      {platformLabel(key)}
    </span>
  );
}

function StatusBadge({ status }) {
  const key = String(status || "active").toLowerCase();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        STATUS_STYLES[key] || STATUS_STYLES.active
      }`}
    >
      {key}
    </span>
  );
}

function SessionRow({
  session,
  selectable,
  selected,
  onToggle,
  onLogout,
  busy,
}) {
  const isActive =
    session.status === "active" || (!session.status && !session.revoked);
  const statusKey = session.status || (isActive ? "active" : "revoked");
  const modelLine =
    session.mobileModel ||
    session.deviceModel ||
    session.deviceManufacturer ||
    null;
  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/80">
      {selectable ? (
        <td className="px-2 py-2.5 align-top">
          {isActive ? (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggle(session.id)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-[#FB565F] focus:ring-[#FB565F]"
              aria-label={`Select session ${session.id}`}
            />
          ) : (
            <span className="inline-block w-4" />
          )}
        </td>
      ) : null}
      <td className="px-2 py-2.5 align-top">
        <div className="flex items-start gap-2">
          {String(session.platform).toLowerCase() === "web" ||
          session.deviceKind === "desktop" ? (
            <FiMonitor className="mt-0.5 shrink-0 text-gray-400" />
          ) : (
            <FiSmartphone className="mt-0.5 shrink-0 text-gray-400" />
          )}
          <div className="min-w-0">
            <p className="font-medium text-gray-800 break-words">
              {deviceSummary(session)}
            </p>
            {modelLine &&
            String(modelLine).toLowerCase() !==
              String(deviceSummary(session)).toLowerCase() ? (
              <p className="mt-0.5 text-[11px] font-semibold text-slate-600">
                Model: {modelLine}
              </p>
            ) : null}
            {session.deviceManufacturer ? (
              <p className="mt-0.5 text-[11px] text-gray-500">
                Brand: {session.deviceManufacturer}
              </p>
            ) : null}
            {deviceKindLabel(session) ? (
              <p className="mt-0.5 text-[11px] font-medium text-gray-500">
                {deviceKindLabel(session)}
              </p>
            ) : null}
            {session.appVersion ? (
              <p className="mt-0.5 text-[11px] text-gray-400">
                App v{session.appVersion}
              </p>
            ) : null}
            <p className="mt-0.5 text-[11px] text-gray-500">
              Current App Version:{" "}
              <span className="font-medium text-slate-700">
                {session.currentAppVersion || "—"}
              </span>
            </p>
            {session.deviceId ? (
              <p className="mt-0.5 max-w-[240px] break-all font-mono text-[10px] text-gray-400">
                {session.deviceId}
              </p>
            ) : null}
          </div>
        </div>
      </td>
      <td className="px-2 py-2.5 align-top text-gray-700">
        <p className="whitespace-nowrap font-medium">{osSummary(session)}</p>
      </td>
      <td className="px-2 py-2.5 align-top">
        <PlatformBadge platform={session.platform} />
      </td>
      <td className="px-2 py-2.5 align-top font-mono text-xs text-gray-600">
        {session.ipAddress || "—"}
      </td>
      <td className="px-2 py-2.5 align-top text-gray-600 whitespace-nowrap">
        {formatDate(session.lastUsedAt || session.createdAt)}
        <p className="mt-0.5 text-[10px] text-gray-400">
          Login {formatDate(session.createdAt)}
        </p>
      </td>
      <td className="px-2 py-2.5 align-top">
        <StatusBadge status={statusKey} />
      </td>
      <td className="px-2 py-2.5 align-top text-right whitespace-nowrap">
        {isActive ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onLogout(session)}
            className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
          >
            <FiLogOut />
            Logout
          </button>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
    </tr>
  );
}

function buildQueryParams(filters, historyPage) {
  return {
    page: historyPage,
    limit: HISTORY_PAGE_SIZE,
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.platform !== "all" ? { platform: filters.platform } : {}),
    ...(filters.status !== "all" ? { status: filters.status } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  };
}

export default function UserLoginDevicesModal({
  open = true,
  user,
  onClose,
  onChanged,
  /** When true, render inline (e.g. User Details tab) instead of a popup. */
  embedded = false,
}) {
  const userId = user?.userId || user?.id;
  const isOpen = embedded ? Boolean(userId) : Boolean(open);
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const displayName =
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    user?.email ||
    (userId ? `User #${userId}` : "User");

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await loginSessionsService.listByUser(
        userId,
        buildQueryParams(filters, historyPage)
      );
      setActive(res?.active || []);
      setHistory(res?.history || []);
      setHistoryTotal(Number(res?.historyTotal) || 0);
      setSelected(new Set());
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load devices"
      );
      setActive([]);
      setHistory([]);
      setHistoryTotal(0);
    } finally {
      setLoading(false);
    }
  }, [userId, historyPage, filters]);

  useEffect(() => {
    if (!isOpen) return;
    setHistoryPage(1);
    setSelected(new Set());
    setFilterDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  }, [isOpen, userId]);

  useEffect(() => {
    if (isOpen && userId) load();
  }, [isOpen, userId, load]);

  const activeIds = useMemo(() => active.map((s) => s.id), [active]);
  const allSelected =
    activeIds.length > 0 && activeIds.every((id) => selected.has(id));
  const historyPages = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));
  const filtersActive =
    filters.search ||
    filters.platform !== "all" ||
    filters.status !== "all" ||
    filters.dateFrom ||
    filters.dateTo;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(activeIds));
  };

  const applyFilters = (e) => {
    e?.preventDefault?.();
    setHistoryPage(1);
    setFilters({ ...filterDraft, search: filterDraft.search.trim() });
  };

  const clearFilters = () => {
    setFilterDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setHistoryPage(1);
  };

  const afterChange = async () => {
    await load();
    if (onChanged) onChanged();
  };

  const handleLogoutOne = async (session) => {
    const ok = await confirmDanger(
      `Log out from ${deviceSummary(session)} (${platformLabel(
        session.platform
      )})? They will need to sign in again on that device.`,
      "Force logout device?"
    );
    if (!ok) return;
    setBusy(true);
    try {
      await loginSessionsService.forceLogoutSession(session.id);
      toast.success("Device logged out");
      await afterChange();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Force logout failed"
      );
    } finally {
      setBusy(false);
    }
  };

  const handleLogoutSelected = async () => {
    const ids = [...selected];
    if (!ids.length) {
      toast.error("Select at least one device");
      return;
    }
    const ok = await confirmDanger(
      `Log out ${ids.length} selected device(s) for ${displayName}?`,
      "Force logout selected?"
    );
    if (!ok) return;
    setBusy(true);
    try {
      await loginSessionsService.forceLogoutSessions(ids);
      toast.success(`${ids.length} device(s) logged out`);
      await afterChange();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Force logout failed"
      );
    } finally {
      setBusy(false);
    }
  };

  const handleLogoutAll = async () => {
    const ok = await confirmDanger(
      `This will revoke every active session for ${displayName}.`,
      "Log out all devices?"
    );
    if (!ok) return;
    setBusy(true);
    try {
      await loginSessionsService.forceLogoutAllForUser(userId);
      toast.success("All devices logged out");
      await afterChange();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!embedded && !open) return null;

  const body = (
    <>
      <form
        onSubmit={applyFilters}
        className="mb-5 grid grid-cols-1 gap-3 rounded-xl border border-gray-100 bg-gray-50/70 p-3 sm:grid-cols-2 lg:grid-cols-6"
      >
        <label className="block sm:col-span-2 lg:col-span-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Search device
          </span>
          <div className="relative mt-1">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-2 text-sm outline-none focus:ring-2 focus:ring-red-200"
              placeholder="Model, OS, IP, device id…"
              value={filterDraft.search}
              onChange={(e) =>
                setFilterDraft((f) => ({ ...f, search: e.target.value }))
              }
            />
          </div>
        </label>
        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Platform
          </span>
          <select
            className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm"
            value={filterDraft.platform}
            onChange={(e) =>
              setFilterDraft((f) => ({ ...f, platform: e.target.value }))
            }
          >
            <option value="all">All</option>
            <option value="web">Web</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Status
          </span>
          <select
            className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm"
            value={filterDraft.status}
            onChange={(e) =>
              setFilterDraft((f) => ({ ...f, status: e.target.value }))
            }
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
            <option value="expired">Expired</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            From
          </span>
          <input
            type="date"
            className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm"
            value={filterDraft.dateFrom}
            onChange={(e) =>
              setFilterDraft((f) => ({ ...f, dateFrom: e.target.value }))
            }
          />
        </label>
        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            To
          </span>
          <input
            type="date"
            className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm"
            value={filterDraft.dateTo}
            onChange={(e) =>
              setFilterDraft((f) => ({ ...f, dateTo: e.target.value }))
            }
          />
        </label>
        <div className="flex flex-wrap items-end gap-2 sm:col-span-2 lg:col-span-6">
          <button
            type="submit"
            className="rounded-lg bg-[#FB565F] px-3 py-2 text-sm font-semibold text-white hover:bg-[#e94a53]"
          >
            Apply filters
          </button>
          <button
            type="button"
            onClick={clearFilters}
            disabled={!filtersActive}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Clear
          </button>
          {filtersActive ? (
            <span className="text-xs text-gray-500 self-center">
              Filters applied to active devices + history
            </span>
          ) : null}
        </div>
      </form>

      {loading && !active.length && !history.length ? (
        <p className="py-10 text-center text-sm text-gray-500">
          Loading devices…
        </p>
      ) : (
        <div className="space-y-6">
          <section>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="text-sm font-bold text-gray-900">
                  Active login devices
                </h4>
                <p className="text-xs text-gray-500">
                  Currently logged in — {active.length} device
                  {active.length === 1 ? "" : "s"}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy || selected.size === 0}
                  onClick={handleLogoutSelected}
                  className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                >
                  <FiLogOut />
                  Logout selected ({selected.size})
                </button>
                <button
                  type="button"
                  disabled={busy || active.length === 0}
                  onClick={handleLogoutAll}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Logout all devices
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-[11px] uppercase tracking-wide text-gray-500">
                    <th className="px-2 py-2.5">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        disabled={!active.length}
                        className="h-4 w-4 rounded border-gray-300 text-[#FB565F] focus:ring-[#FB565F]"
                        aria-label="Select all active devices"
                      />
                    </th>
                    <th className="px-2 py-2.5 font-semibold">Device / model</th>
                    <th className="px-2 py-2.5 font-semibold">OS</th>
                    <th className="px-2 py-2.5 font-semibold">Platform</th>
                    <th className="px-2 py-2.5 font-semibold">IP</th>
                    <th className="px-2 py-2.5 font-semibold">Last active</th>
                    <th className="px-2 py-2.5 font-semibold">Status</th>
                    <th className="px-2 py-2.5 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {active.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-3 py-8 text-center text-gray-500"
                      >
                        No active devices match these filters.
                      </td>
                    </tr>
                  ) : (
                    active.map((session) => (
                      <SessionRow
                        key={session.id}
                        session={session}
                        selectable
                        selected={selected.has(session.id)}
                        onToggle={toggleOne}
                        onLogout={handleLogoutOne}
                        busy={busy}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <div className="mb-3">
              <h4 className="text-sm font-bold text-gray-900">
                Previous login records
              </h4>
              <p className="text-xs text-gray-500">
                Revoked or expired only — {historyTotal} record
                {historyTotal === 1 ? "" : "s"}
              </p>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-[11px] uppercase tracking-wide text-gray-500">
                    <th className="px-2 py-2.5 font-semibold">Device / model</th>
                    <th className="px-2 py-2.5 font-semibold">OS</th>
                    <th className="px-2 py-2.5 font-semibold">Platform</th>
                    <th className="px-2 py-2.5 font-semibold">IP</th>
                    <th className="px-2 py-2.5 font-semibold">When</th>
                    <th className="px-2 py-2.5 font-semibold">Status</th>
                    <th className="px-2 py-2.5 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-3 py-8 text-center text-gray-500"
                      >
                        No login history matches these filters.
                      </td>
                    </tr>
                  ) : (
                    history.map((session) => (
                      <SessionRow
                        key={session.id}
                        session={session}
                        selectable={false}
                        selected={false}
                        onToggle={() => {}}
                        onLogout={handleLogoutOne}
                        busy={busy}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {historyPages > 1 ? (
              <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-600">
                <span>
                  Page {historyPage} of {historyPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={historyPage <= 1 || loading}
                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 font-semibold hover:bg-gray-50 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={historyPage >= historyPages || loading}
                    onClick={() =>
                      setHistoryPage((p) => Math.min(historyPages, p + 1))
                    }
                    className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 font-semibold hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="bg-white rounded-3xl shadow-md border border-slate-100 p-4 md:p-6">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-800">Login devices</h3>
            <p className="text-sm text-slate-500">
              {displayName}
              {user?.email ? ` · ${user.email}` : ""}
              {userId ? ` · #${userId}` : ""}
            </p>
          </div>
          <button
            type="button"
            disabled={busy || loading}
            onClick={() => load()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
        {body}
      </div>
    );
  }

  return (
    <Modal
      open={open}
      size="lg"
      onClose={onClose}
      title="Login devices"
      subtitle={`${displayName}${user?.email ? ` · ${user.email}` : ""}${
        userId ? ` · #${userId}` : ""
      }`}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          <button
            type="button"
            disabled={busy || loading}
            onClick={() => load()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </>
      }
    >
      {body}
    </Modal>
  );
}
